import BeelditDateTimeInput from "@beeldit/core/components/BeelditDateTimeInput";
import BeelditFileInput from "@beeldit/core/components/BeelditFileInput";
import BeelditSwitchInput from "@beeldit/core/components/BeelditSwitchInput";
import { RowAction } from "@beeldit/core/enums/RowAction.enum";
import { TableAction } from "@beeldit/core/enums/TableAction.enum";
import CustomerSelector from "@main/customers/components/CustomerSelector";
import TherapistSelector from "@main/therapists/components/TherapistSelector";
import TreatmentSelector from "@main/treatments/components/TreatmentSelector";
import { Pencil, Plus, Trash } from "@phosphor-icons/react";
import { t } from "i18next";

const config = {
  modelName: "session",
  modelPluralName: "sessions",
  endpoint: "sessions",
  detailsFunctionPath: "/panel/1/1/sessions/",
  // Formulario a pantalla completa en móvil
  modalFullScreen: "md-down",
  indexQueryParams: {
    relations: ["treatment"],
  },
  tableConfig: {
    index: null,
    actions: [
      {
        name: TableAction.CREATE,
        label: "create",
        showLabel: true,
        allowed: true,
        show: true,
        action: null,
        icon: <Plus size={20} />,
      },
    ],
    rowActions: [
      {
        name: RowAction.EDIT,
        label: "edit",
        icon: <Pencil size={20} />,
        allowed: true,
        show: true,
        action: (element: any) => {
          console.log("edit", element);
        },
      },
      {
        name: RowAction.DELETE,
        label: "delete",
        icon: <Trash size={20} />,
        allowed: true,
        show: true,
        action: (element: any) => {
          console.log("delete", element);
        },
      },
    ],
    rowConfiguration: {
      class: (element: any) => {
        return null;
      },
    },
    columns: [
      {
        name: "id",
        label: "#",
        key: "id",
        type: "number",
        show: true,
        allowOrder: true,
      },
      {
        name: "start_date_time",
        label: "start_date_time",
        key: "start_date_time",
        type: "datetime",
        show: true,
        allowOrder: true,
      },
      {
        name: "status",
        label: "status",
        key: "status",
        type: "translation",
        translation_prefix: "session_status",
        show: true,
        allowOrder: true,
      },
      {
        name: "treatment",
        label: "treatment",
        key: "treatment.name",
        type: "string",
        show: true,
        allowOrder: true,
      },
    ],
    pagination: {
      enabled: true,
      back: true,
    },
  },
  filterConfig: {
    type: "object",
    properties: {
      search: {
        type: "string",
      },
    },
  },
  filterUIConfig: {
    uiScheme: {
      className: "filters-container",
      search: {
        "ui:options": {
          label: false, // Oculta el título del campo
          placeholder: "Buscar...",
        },
      },
    },
    widgets: {},
  },
  formSchema: {
    type: "object",
    properties: {
      with_treatment: {
        type: "boolean",
        title: t("with_treatment"),
        default: true,
      },
      customer_id: {
        type: "number",
        title: t("customer_id"),
      },
      treatment_id: {
        type: ["number", "null"],
        title: t("treatment_id"),
      },
      internal_comment: {
        type: ["string", "null"],
        title: t("internal_comment"),
        format: "textarea",
      },
      start_date_time: {
        type: "string",
        title: t("start_date_time"),
        format: "date-time",
      },
      duration: {
        type: "number",
        title: t("duration"),
        description: "Duración de la sesión en minutos",
        default: 60,
        minimum: 15,
        maximum: 240,
      },
      therapist_id: {
        type: "number",
        title: t("therapist_id"),
      },
      status: {
        type: ["number", "null"],
        title: t("status"),
        default: 0,
        oneOf: [
          {
            const: 0,
            title: "Pendiente",
          },
          {
            const: 1,
            title: "Realizada",
          },
          // Cancelada
          {
            const: 2,
            title: "Cancelada",
          },
          // No ha venido, ha avisado
          {
            const: 3,
            title: "No ha venido, ha avisado",
          },
          // No ha venido, no ha avisado
          {
            const: 4,
            title: "No ha venido, no ha avisado",
          },
          // Cita recuperada
          {
            const: 5,
            title: "Cita recuperada",
          },
        ],
      },
      review: {
        type: "number",
        title: t("review"),
        default: 0,
        minimum: 0,
        maximum: 5,
      },
      work_in: {
        type: ["string", "null"],
        title: "Hemos trabajado",
        format: "textarea",
      },
      homework: {
        type: ["string", "null"],
        title: "Reforzar en casa",
        format: "textarea",
      },
      video: {
        type: "array",
        titlet: t("video"),
        description: "(Video de la sesión en formato mp4, avi, mov, etc.)",
        items: {
          type: "object",
          properties: {
            filename: { type: "string", title: "filename" },
            file_name: { type: "string", title: "file_name" },
            url: { type: "string", title: "url" },
            name: { type: "string", title: "name" },
            id: { type: "number", title: "id" },
            mime_type: { type: "string", title: "mime_type" },
            size: { type: "number", title: "size" },
          },
        },
      },
      cancellation_reason: {
        type: ["string", "null"],
        title: t("cancellation_reason") || "Cancellation reason",
        format: "textarea",
        show: (element: any) => {
          return element && Number(element.status) === 2;
        },
      },
      cancelled_at: {
        type: ["string", "null"],
        title: t("cancelled_at") || "Cancelled at",
        format: "date-time",
        show: (element: any) => {
          return element && Number(element.status) === 2;
        },
      },
    },
  },
  uiFormSchema: {
    "ui:submitButtonOptions": {
      props: {
        disabled: false,
        className: "btn btn-info",
      },
      norender: true,
    },
    with_treatment: {
      "ui:widget": BeelditSwitchInput,
    },
    customer_id: {
      "ui:widget": CustomerSelector,
    },
    treatment_id: {
      "ui:widget": TreatmentSelector,
    },
    therapist_id: {
      "ui:widget": TherapistSelector,
    },
    start_date_time: {
      "ui:widget": BeelditDateTimeInput,
    },
    video: {
      "ui:widget": BeelditFileInput,
    },
    work_in: {
      "ui:options": {
        label: true,
        className: "red-label-field",
      },
    },
    homework: {
      "ui:options": {
        label: true,
        className: "red-label-field",
      },
    },
    colsSchema: [
      {
        cols: "col-12",
        elements: [
          [
            {
              name: "with_treatment",
              cols: "col-6",
            },
            {
              name: "customer_id",
              cols: "col-6",
            },
          ],
          [
            {
              name: "treatment_id",
              cols: "col-6",
            },
            {
              name: "internal_comment",
              cols: "col-6",
            },
          ],
          [
            {
              name: "start_date_time",
              cols: "col-8",
            },
            {
              name: "duration",
              cols: "col-4",
            },
          ],
          [
            {
              name: "therapist_id",
              cols: "col-6",
            },
            {
              name: "status",
              cols: "col-6",
            },
          ],
          [
            {
              name: "cancellation_reason",
              cols: "col-12",
            },
            {
              name: "cancelled_at",
              cols: "col-6",
            },
          ],
          [
            {
              name: "review",
              cols: "col-6",
            },
          ],
          [
            {
              name: "work_in",
              cols: "col-12",
            },
          ],
          [
            {
              name: "homework",
              cols: "col-12",
            },
          ],
          [
            {
              name: "video",
              cols: "col-12",
            },
          ],
        ],
      },
    ],
  },
};
export default config;
